import React, { useRef, useState } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Button,
  Dropdown,
  Input,
  Option,
  Spinner,
  Text,
  Toast,
  ToastBody,
  ToastTitle,
  Toaster,
  useId,
  useToastController,
} from '@fluentui/react-components';
import {
  Camera20Regular,
  ArrowUpload20Regular,
  Delete16Regular,
  DocumentPdf24Regular,
} from '@fluentui/react-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/apiFetch';
import { useIsMobile } from '../hooks/useIsMobile';
import { prepareImageUpload, readAsDataURL } from '../lib/imageUpload';
import { rotateAttachment } from '../lib/rotateAttachment';
import FloatingImageViewer from './FloatingImageViewer';

// Mirror the backend guards (app.py) so oversized/unsupported files are caught
// instantly with a clear message instead of a round-trip 413/415. The picker's
// accept filter narrows it client-side; this also rejects e.g. iOS HEIC, which
// the <img> thumbnail/viewer can't render.
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10 MB
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const PDF_TYPE = 'application/pdf';

// True for an attachment whose bytes are a PDF (by MIME, with a filename
// fallback for older rows that didn't persist a type). PDFs can't render in an
// <img>, so they get a document tile and open in the viewer's <iframe> mode.
const isPdfAttachment = (att: { file_type?: string | null; name?: string | null }): boolean =>
  (att.file_type || '').toLowerCase() === PDF_TYPE || /\.pdf$/i.test(att.name || '');

export interface OrderAttachment {
  id: string;
  kind: string;
  name: string;
  file_type: string | null;
  size_bytes: number | null;
  category: string | null;
  caption: string | null;
  created_at: string | null;
  signed_url: string | null;
}

const useStyles = makeStyles({
  root: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
  },
  hint: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  uploadRow: {
    display: 'flex',
    ...shorthands.gap('8px'),
    flexWrap: 'wrap',
  },
  // Category picker for the next upload (metadata mode only).
  categoryRow: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('2px'),
  },
  categoryLabel: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
  },
  // Shown only while a file is dragged over the section. Sits above the content
  // as a full-cover confirmation; pointer-events stay off so the drag-counter on
  // root keeps tracking enter/leave without the overlay itself triggering them.
  dragOverlay: {
    position: 'absolute',
    top: '0',
    right: '0',
    bottom: '0',
    left: '0',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.gap('4px'),
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('2px', 'dashed', tokens.colorBrandStroke1),
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    pointerEvents: 'none',
  },
  dragOverlayHint: {
    fontSize: '13px',
    fontWeight: tokens.fontWeightSemibold,
  },
  thumbs: {
    display: 'flex',
    ...shorthands.gap('8px'),
    flexWrap: 'wrap',
  },
  thumbWrap: {
    position: 'relative',
    // Sized off a CSS var so the host can request larger tiles (e.g. the
    // full-screen manager) without inline styles; defaults to the compact 72px.
    width: 'var(--ag-thumb, 72px)',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('2px'),
  },
  thumb: {
    width: 'var(--ag-thumb, 72px)',
    height: 'var(--ag-thumb, 72px)',
    objectFit: 'cover',
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    cursor: 'pointer',
    display: 'block',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  // PDF stand-in tile (PDFs can't render in an <img>). Same footprint as a
  // thumbnail; clicking opens the file in a new tab.
  thumbDoc: {
    width: 'var(--ag-thumb, 72px)',
    height: 'var(--ag-thumb, 72px)',
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.gap('2px'),
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground2,
  },
  thumbDocLabel: {
    fontSize: '10px',
    fontWeight: tokens.fontWeightSemibold,
  },
  thumbDelete: {
    position: 'absolute',
    top: '-8px',
    right: '-8px',
    minWidth: 'unset',
    width: '24px',
    height: '24px',
    ...shorthands.padding('0'),
    ...shorthands.borderRadius('50%'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
  },
  // Category chip pinned to the thumbnail's bottom-left (metadata mode).
  thumbCategory: {
    position: 'absolute',
    left: '2px',
    bottom: '20px',
    maxWidth: '68px',
    ...shorthands.padding('0', '4px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackgroundInverted,
    color: tokens.colorNeutralForegroundInverted,
    fontSize: '10px',
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: '14px',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  thumbCaption: {
    width: 'var(--ag-thumb, 72px)',
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Caption + category editor in the floating viewer footer (metadata mode).
  editor: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
  },
  editorField: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('2px'),
  },
  editorLabel: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
  },
  editorActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    ...shorthands.gap('8px'),
  },
  hiddenInput: {
    display: 'none',
  },
});

interface AttachmentGalleryProps {
  /** TWM order UUID. Null for a brand-new order that hasn't been saved yet. */
  orderId: string | null;
  /** Attachment kind discriminator (e.g. 'tonnage_receipt', 'order_photo'). */
  kind: string;
  /** Singular noun for copy/toasts ("receipt", "photo"). Default "file". */
  noun?: string;
  /** Primary upload-button label. Defaults to `Add ${noun}`. */
  uploadLabel?: string;
  /** Override the empty-state hint. */
  emptyHint?: string;
  /** Suppress the empty-state hint entirely (compact inline galleries). */
  hideEmptyHint?: boolean;
  /** When provided, enables metadata mode: a category picker for new uploads
   *  plus per-attachment caption + category editing in the viewer. Omit for the
   *  plain receipt gallery. */
  categories?: string[];
  /** Allow PDF uploads alongside images (e.g. emailed scale tickets). PDFs
   *  aren't downscaled and open in a new tab rather than the floating viewer.
   *  Defaults off so photo galleries stay image-only. */
  acceptPdf?: boolean;
  /** Scope the gallery to a single order line (a weighed container). When set,
   *  the gallery only lists/uploads attachments tied to this Booqable line id,
   *  and stamps `line_id`/`line_label` on new uploads. Omit for an order-wide
   *  gallery. */
  lineId?: string;
  /** Snapshot of the line's name, persisted with uploads for display/email. */
  lineLabel?: string;
  /** Customer name for the order. When present on a tonnage_receipt gallery,
   *  new uploads are named "[Customer] - [Line] - Tonnage Receipt". */
  customerName?: string | null;
  /** Thumbnail edge length in px. Defaults to the compact 72px used in the
   *  sidebar; the full-screen manager passes a larger value for a roomy grid. */
  thumbSize?: number;
  /** How a clicked thumbnail previews. 'floating' (default) opens the draggable
   *  FloatingImageViewer over the page — used inline beside the form so you can
   *  read a receipt while typing. 'embedded' renders the preview in-flow inside
   *  the host (the Photos & Attachments dialog) so it stays within the modal. */
  previewMode?: 'floating' | 'embedded';
  /** Hide all upload affordances (buttons, category picker, drag-to-upload),
   *  leaving a view/delete-only gallery. Used where uploads belong elsewhere —
   *  e.g. tonnage receipts are captured per weighed line, not order-wide. */
  disableUpload?: boolean;
}

const AttachmentGallery: React.FC<AttachmentGalleryProps> = ({
  orderId,
  kind,
  noun = 'file',
  uploadLabel,
  emptyHint,
  hideEmptyHint = false,
  categories,
  acceptPdf = false,
  lineId,
  lineLabel,
  customerName,
  thumbSize,
  previewMode = 'floating',
  disableUpload = false,
}) => {
  const styles = useStyles();
  const isMobile = useIsMobile();
  const embeddedPreview = previewMode === 'embedded';
  // Allowed upload MIME types + the picker's accept filter, widened for PDFs
  // when the host opts in. The camera input stays image-only (you can't snap a
  // PDF) — only the file picker offers PDF.
  const allowedTypes = acceptPdf ? [...ALLOWED_IMAGE_TYPES, PDF_TYPE] : ALLOWED_IMAGE_TYPES;
  const uploadAccept = acceptPdf ? 'image/*,application/pdf' : 'image/*';
  const queryClient = useQueryClient();
  const toasterId = useId('attachment-gallery-toaster');
  const { dispatchToast } = useToastController(toasterId);

  const metadata = Array.isArray(categories) && categories.length > 0;

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  // dragenter/dragleave bubble from child elements too, so a single boolean
  // flickers as the cursor crosses buttons/inputs. Count nesting depth instead
  // and only clear the overlay when we've left the section entirely.
  const dragDepth = useRef(0);
  const [viewer, setViewer] = useState<OrderAttachment | null>(null);
  // Category applied to the NEXT upload (metadata mode). Seeds to the first
  // option so an upload always lands in a real category.
  const [uploadCategory, setUploadCategory] = useState<string>(
    metadata ? categories![0] : '',
  );

  const attachmentsQuery = useQuery<{ data: OrderAttachment[] }>({
    queryKey: ['order-attachments', orderId, kind, lineId ?? null],
    queryFn: async () => {
      const lineParam = lineId ? `&line_id=${encodeURIComponent(lineId)}` : '';
      const res = await apiFetch(
        `/orders/${encodeURIComponent(orderId!)}/attachments?kind=${encodeURIComponent(kind)}${lineParam}`,
      );
      if (!res.ok) throw new Error(`attachments fetch failed: ${res.status}`);
      return res.json();
    },
    enabled: !!orderId,
    staleTime: 60 * 1000,
  });
  const attachments = attachmentsQuery.data?.data ?? [];

  // Invalidate the whole order's attachment family (all kinds + line scopes), so
  // a per-line upload also refreshes the order-wide list the Send dialog reads.
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['order-attachments', orderId] });

  const errToast = (title: string, err: unknown) =>
    dispatchToast(
      <Toast>
        <ToastTitle>{title}</ToastTitle>
        <ToastBody>{err instanceof Error ? err.message : 'Unknown error'}</ToastBody>
      </Toast>,
      { intent: 'error' },
    );

  const handleFile = async (file: File | undefined) => {
    if (!file || !orderId) return;
    if (!allowedTypes.includes(file.type)) {
      dispatchToast(
        <Toast>
          <ToastTitle>Unsupported file type</ToastTitle>
          <ToastBody>
            {acceptPdf
              ? 'Use a JPEG, PNG, or WebP image, or a PDF.'
              : 'Use a JPEG, PNG, or WebP image.'}
          </ToastBody>
        </Toast>,
        { intent: 'error' },
      );
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (uploadInputRef.current) uploadInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      dispatchToast(
        <Toast>
          <ToastTitle>File too large</ToastTitle>
          <ToastBody>{`${noun[0].toUpperCase()}${noun.slice(1)}s must be under 10 MB.`}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (uploadInputRef.current) uploadInputRef.current.value = '';
      return;
    }
    setUploading(true);
    try {
      // Downscale large images before upload: base64-in-JSON inflates the body
      // ~1.37×, and Vercel 413s request bodies over ~4.5 MB before Flask sees
      // them. prepareImageUpload returns the original untouched when it's
      // already small, or a downscaled JPEG that fits under the cap. PDFs can't
      // be downscaled, so they go up as-is — scale tickets are small, well under
      // the cap. (Day-2: direct-to-Supabase signed upload removes this limit.)
      const prepared =
        file.type === PDF_TYPE
          ? { dataUrl: await readAsDataURL(file), fileType: PDF_TYPE, name: file.name || `${noun}.pdf` }
          : await prepareImageUpload(file);
      // Tonnage receipts get a consistent, human-readable name so the attachment
      // list and downloads read "[Customer] - [Line] - Tonnage Receipt.<ext>".
      // Falls back to the raw upload name when the customer/line context is
      // missing (e.g. an order-wide gallery). The extension is preserved from the
      // prepared file type so downloads stay openable.
      const uploadName = (() => {
        const fallback = prepared.name || `${noun}.jpg`;
        if (kind !== 'tonnage_receipt' || !customerName || !lineLabel) return fallback;
        const ext =
          prepared.fileType === PDF_TYPE ? '.pdf'
          : prepared.fileType === 'image/png' ? '.png'
          : prepared.fileType === 'image/webp' ? '.webp'
          : '.jpg';
        const clean = (s: string) => s.replace(/[\\/]+/g, '-').trim();
        return `${clean(customerName)} - ${clean(lineLabel)} - Tonnage Receipt${ext}`;
      })();
      const res = await apiFetch(`/orders/${encodeURIComponent(orderId)}/attachments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: uploadName,
          file_type: prepared.fileType || 'image/jpeg',
          data_base64: prepared.dataUrl,
          kind,
          ...(metadata ? { category: uploadCategory } : {}),
          ...(lineId ? { line_id: lineId, line_label: lineLabel ?? '' } : {}),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Upload failed (HTTP ${res.status})`);
      }
      invalidate();
      dispatchToast(
        <Toast>
          <ToastTitle>{`${noun[0].toUpperCase()}${noun.slice(1)} uploaded`}</ToastTitle>
        </Toast>,
        { intent: 'success' },
      );
    } catch (err) {
      errToast(`Could not upload ${noun}`, err);
    } finally {
      setUploading(false);
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (uploadInputRef.current) uploadInputRef.current.value = '';
    }
  };

  // Only react to drags that actually carry files (ignore text/element drags).
  const dragHasFiles = (e: React.DragEvent) =>
    Array.from(e.dataTransfer?.types ?? []).includes('Files');

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    if (!dragging) setDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!dragHasFiles(e)) return;
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (uploading) return;
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleDelete = async (att: OrderAttachment) => {
    if (!orderId) return;
    try {
      const res = await apiFetch(
        `/orders/${encodeURIComponent(orderId)}/attachments/${encodeURIComponent(att.id)}`,
        { method: 'DELETE' },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Delete failed (HTTP ${res.status})`);
      }
      invalidate();
    } catch (err) {
      errToast(`Could not delete ${noun}`, err);
    }
  };

  // Rotate the open attachment by `degrees` (clockwise) and overwrite its bytes
  // in place — same row, same storage key, fresh signed URL. Rethrows so the
  // viewer keeps the pending rotation for a retry.
  const handleSaveRotation = async (att: OrderAttachment, degrees: number) => {
    if (!orderId || !att.signed_url) return;
    try {
      const prepared = await rotateAttachment({
        url: att.signed_url,
        name: att.name,
        fileType: att.file_type,
        degrees,
      });
      const res = await apiFetch(
        `/orders/${encodeURIComponent(orderId)}/attachments/${encodeURIComponent(att.id)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data_base64: prepared.dataUrl, file_type: prepared.fileType }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Save failed (HTTP ${res.status})`);
      }
      const body = await res.json().catch(() => ({}));
      const updated = (body?.data as OrderAttachment) ?? att;
      setViewer(updated);
      invalidate();
      dispatchToast(
        <Toast>
          <ToastTitle>{`${noun[0].toUpperCase()}${noun.slice(1)} rotated`}</ToastTitle>
        </Toast>,
        { intent: 'success' },
      );
    } catch (err) {
      errToast(`Could not rotate ${noun}`, err);
      throw err;
    }
  };

  if (!orderId) {
    return <div className={styles.hint}>{`Save the order before adding a ${noun}.`}</div>;
  }

  // In embedded mode the open preview takes over the gallery area (replacing the
  // upload row + thumbnail grid), so it reads as a single in-modal view rather
  // than a panel layered over the thumbnails.
  const previewing = embeddedPreview && !!viewer;

  const dropNoun = acceptPdf ? 'file' : 'image';
  const defaultEmpty = isMobile
    ? `No ${noun} yet — take a photo, upload a file, or drag a ${dropNoun} here.`
    : `No ${noun} yet — use the buttons above, or drag a ${dropNoun} anywhere in this section.`;

  return (
    <div
      className={styles.root}
      // Drive thumb dimensions via a CSS var (set only when the host overrides
      // the default) so makeStyles still owns all the styling.
      style={thumbSize ? ({ ['--ag-thumb' as string]: `${thumbSize}px` } as React.CSSProperties) : undefined}
      onDragEnter={disableUpload ? undefined : handleDragEnter}
      onDragOver={disableUpload ? undefined : (e) => {
        if (dragHasFiles(e)) e.preventDefault();
      }}
      onDragLeave={disableUpload ? undefined : handleDragLeave}
      onDrop={disableUpload ? undefined : handleDrop}
    >
      {dragging && (
        <div className={styles.dragOverlay} aria-hidden>
          <ArrowUpload20Regular />
          <Text className={styles.dragOverlayHint}>Drop {dropNoun} to upload</Text>
        </div>
      )}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className={styles.hiddenInput}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <input
        ref={uploadInputRef}
        type="file"
        accept={uploadAccept}
        className={styles.hiddenInput}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {metadata && !disableUpload && !previewing && (
        <div className={styles.categoryRow}>
          <Text className={styles.categoryLabel}>Category for new uploads</Text>
          <Dropdown
            size="large"
            value={uploadCategory}
            selectedOptions={[uploadCategory]}
            onOptionSelect={(_, data) => {
              if (data.optionValue) setUploadCategory(data.optionValue);
            }}
          >
            {categories!.map((c) => (
              <Option key={c} value={c}>
                {c}
              </Option>
            ))}
          </Dropdown>
        </div>
      )}

      {!disableUpload && !previewing && (
        <div className={styles.uploadRow}>
          {isMobile && (
            <Button
              appearance="primary"
              icon={uploading ? <Spinner size="tiny" /> : <Camera20Regular />}
              disabled={uploading}
              onClick={() => cameraInputRef.current?.click()}
            >
              Take photo
            </Button>
          )}
          <Button
            appearance={isMobile ? 'secondary' : 'primary'}
            icon={uploading && !isMobile ? <Spinner size="tiny" /> : <ArrowUpload20Regular />}
            disabled={uploading}
            onClick={() => uploadInputRef.current?.click()}
          >
            {isMobile ? 'Upload' : uploadLabel || `Add ${noun}`}
          </Button>
        </div>
      )}

      {previewing ? null : attachmentsQuery.isLoading ? (
        <Spinner size="extra-small" />
      ) : attachments.length > 0 ? (
        <div className={styles.thumbs}>
          {attachments.map((att) =>
            att.signed_url ? (
              <div key={att.id} className={styles.thumbWrap}>
                {isPdfAttachment(att) ? (
                  <div
                    className={styles.thumbDoc}
                    title={`${att.caption || att.name} — click to view`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setViewer(att)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') setViewer(att);
                    }}
                  >
                    <DocumentPdf24Regular />
                    <span className={styles.thumbDocLabel}>PDF</span>
                  </div>
                ) : (
                  <img
                    className={styles.thumb}
                    src={att.signed_url}
                    alt={att.caption || att.name}
                    title={`${att.caption || att.name} — click to view`}
                    onClick={() => setViewer(att)}
                  />
                )}
                {metadata && att.category && (
                  <span className={styles.thumbCategory} title={att.category}>
                    {att.category}
                  </span>
                )}
                <Button
                  className={styles.thumbDelete}
                  appearance="subtle"
                  size="small"
                  icon={<Delete16Regular />}
                  title={`Delete ${noun}`}
                  aria-label={`Delete ${noun}`}
                  onClick={() => handleDelete(att)}
                />
                {metadata && att.caption && (
                  <span className={styles.thumbCaption} title={att.caption}>
                    {att.caption}
                  </span>
                )}
              </div>
            ) : null,
          )}
        </div>
      ) : hideEmptyHint ? null : (
        <div className={styles.hint}>{emptyHint || defaultEmpty}</div>
      )}

      <FloatingImageViewer
        open={!!viewer}
        embedded={embeddedPreview}
        src={viewer?.signed_url ?? null}
        title={viewer?.caption || viewer?.name}
        isPdf={!!viewer && isPdfAttachment(viewer)}
        onSaveRotation={viewer ? (degrees) => handleSaveRotation(viewer, degrees) : undefined}
        onClose={() => setViewer(null)}
        footer={
          metadata && viewer ? (
            <CaptionCategoryEditor
              key={viewer.id}
              orderId={orderId}
              kind={kind}
              attachment={viewer}
              categories={categories!}
              onSaved={(updated) => {
                setViewer(updated);
                invalidate();
              }}
              onError={(err) => errToast('Could not save changes', err)}
            />
          ) : undefined
        }
      />
      <Toaster toasterId={toasterId} position="top" />
    </div>
  );
};

interface CaptionCategoryEditorProps {
  orderId: string;
  kind: string;
  attachment: OrderAttachment;
  categories: string[];
  onSaved: (updated: OrderAttachment) => void;
  onError: (err: unknown) => void;
}

// Caption + category editor shown in the viewer footer. Local draft state keyed
// (via `key={viewer.id}`) so switching photos re-seeds cleanly. PATCHes the
// attachment and hands the fresh row back up so the thumb + viewer title update.
const CaptionCategoryEditor: React.FC<CaptionCategoryEditorProps> = ({
  orderId,
  kind,
  attachment,
  categories,
  onSaved,
  onError,
}) => {
  const styles = useStyles();
  const [caption, setCaption] = useState(attachment.caption ?? '');
  const [category, setCategory] = useState(attachment.category ?? categories[0]);
  const [saving, setSaving] = useState(false);

  const dirty = caption !== (attachment.caption ?? '') || category !== (attachment.category ?? categories[0]);

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const res = await apiFetch(
        `/orders/${encodeURIComponent(orderId)}/attachments/${encodeURIComponent(attachment.id)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ caption, category }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Save failed (HTTP ${res.status})`);
      }
      const body = await res.json().catch(() => ({}));
      onSaved((body?.data as OrderAttachment) ?? { ...attachment, caption, category });
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.editor}>
      <div className={styles.editorField}>
        <Text className={styles.editorLabel}>Category</Text>
        <Dropdown
          size="large"
          value={category}
          selectedOptions={[category]}
          onOptionSelect={(_, data) => {
            if (data.optionValue) setCategory(data.optionValue);
          }}
        >
          {categories.map((c) => (
            <Option key={c} value={c}>
              {c}
            </Option>
          ))}
        </Dropdown>
      </div>
      <div className={styles.editorField}>
        <Text className={styles.editorLabel}>Caption</Text>
        <Input
          size="large"
          value={caption}
          onChange={(_, d) => setCaption(d.value)}
          placeholder="Add a caption…"
        />
      </div>
      <div className={styles.editorActions}>
        <Button appearance="primary" disabled={!dirty || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

export default AttachmentGallery;
